import { type FC } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { Formik } from 'formik'

import { Card, Skeleton, Stack, StackColumn, useSnackbar } from '@dhl-ui/components'
import { DocumentTitle } from '@dhl/receiver-a11y'
import { FlexibleListItemContainer, FlexibleListItemSkeleton } from '@dhl/receiver-design'
import { useSpinnerDelay } from '@dhl/use-spinner-delay'

import { PortalSubPage } from '~/components-page/Page/PortalSubPage.js'
import { links } from '~/links.js'
import { FormContent } from '~/profile/components/FormContent.js'
import { useGetAgreedPlacePreference } from '../hooks/useGetAgreedPlacePreference.js'
import {
  ToDoorPreferenceForm,
  type ToDoorPreferenceFormValues,
  useCreateToDoorPreferenceValidationSchema,
} from './components/ToDoorPreferenceForm.js'
import { useSubmitToDoorPreference } from './hooks/useSubmitToDoorPreference.js'
import { useNavigateWithReferrer } from '../hooks/useNavigateWithReferrer.js'
import { Form } from '~/components/Form.js'

type RouteParams = {
  addressId: string
}

export const ToDoorPreferenceSkeleton: FC = () => (
  <StackColumn gap={3}>
    <Card hasPaddingTop>
      <Skeleton variant="rectangular" width={210} height={24} sx={{ mx: 2, my: 3 }} />
      <Card.Content noPaddingTop>
        <Stack spacing={3}>
          <FlexibleListItemContainer>
            {Array.from({ length: 2 }).map((_, index) => (
              /* eslint-disable-next-line react/no-array-index-key */
              <FlexibleListItemSkeleton key={index} slotProps={{ showSubheader: false }} value={`${index}`} />
            ))}
          </FlexibleListItemContainer>

          <Stack spacing={1}>
            <Skeleton variant="rectangular" width="90%" height={16} />
            <Skeleton variant="rectangular" width="40%" height={16} />
          </Stack>
        </Stack>
      </Card.Content>
    </Card>
  </StackColumn>
)

export const ToDoorPreferenceSetupPage: FC = () => {
  const { addressId } = useParams<RouteParams>()
  const navigate = useNavigate()
  const { t } = useTranslation()
  const { initialFormValues, isLoading, preference } = useGetAgreedPlacePreference(addressId)
  const { validationSchema } = useCreateToDoorPreferenceValidationSchema()
  const { submitToDoorPreference } = useSubmitToDoorPreference(addressId)
  const { enqueueSnackbar } = useSnackbar()
  const location = useLocation()
  const navigateWithReferrer = useNavigateWithReferrer()
  const showLoadingState = useSpinnerDelay(isLoading)

  const handleBack = () => {
    navigate(links.deliveryPreferenceDetailsLink({ addressId }), { state: location.state })
  }

  const handleSubmit = async (values: ToDoorPreferenceFormValues) => {
    try {
      const result = await submitToDoorPreference(values)

      if (result.status === 'partial') {
        enqueueSnackbar(t('portal.profile.preferences.photoUploadFailed'), { severity: 'warning' })
      } else {
        enqueueSnackbar(t('portal.profile.preferences.preferenceSavedSuccessfully'), { severity: 'success' })
      }

      navigateWithReferrer()
    } catch {
      enqueueSnackbar(t('common.pleaseTryAgainErrorMessage'), { severity: 'error' })
    }
  }

  const initialValues: ToDoorPreferenceFormValues = {
    ...initialFormValues,
    fallback: preference?.activeSetting?.fallback?.preferenceType ?? undefined,
  }

  return (
    <PortalSubPage
      isMaximizedCentered
      headerBackground="subtle"
      data-testid="setup-to-door-preference-page"
      hasMaxSize="MD"
      title={t('portal.profile.preferences.options.DoorDelivery.title')}
      onBack={handleBack}
    >
      <DocumentTitle title={t('portal.profile.preferences.options.DoorDelivery.title')} />

      <FormContent>
        {showLoadingState && <ToDoorPreferenceSkeleton />}

        {!showLoadingState && (
          <Formik
            enableReinitialize
            initialValues={initialValues}
            onSubmit={handleSubmit}
            validationSchema={validationSchema}
          >
            <Form id="delivery-preference-to-door-form">
              <ToDoorPreferenceForm />
            </Form>
          </Formik>
        )}

        <FormContent.Buttons>
          <FormContent.Button form="delivery-preference-to-door-form" type="submit" variant="contained">
            {t('portal.profile.preferences.saveButtonText')}
          </FormContent.Button>
        </FormContent.Buttons>
      </FormContent>
    </PortalSubPage>
  )
}
